// Analogpitch bundles: the product-page picker ([data-ap-picker]), one-click "+ Add" in the cart
// ([data-ap-bundle-add]), "swap my packs for the full bundle" ([data-ap-bundle-swap]) and "add the packs I'm
// missing" ([data-ap-bundle-add-many], blocks/ap-complete-collection.liquid).
// Every change is announced the way Horizon's product form does (CartLinesUpdateEvent) with freshly fetched cart
// section HTML, so drawer, cart icon and cart page refresh without a page reload, and the drawer opens by itself
// (auto-open is on). Alva's consent travels with the lines – see pageConsent below.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

// Alva Digital Downloads keeps the buyer's withdrawal-right consent as two hidden line properties. Its own script
// only writes them for the product its checkbox belongs to, so consent given on a product page is copied here to
// every product added from that page (bundle picker, "add the missing packs"), kept when packs are swapped for a
// full bundle, and inherited by "+ Add" in the cart when the cart already has consented lines. Owner: a ticked box
// must follow all the way to the order, so the buyer gets the contract in Alva's email. Unticked = no properties
// = Alva handles it in the download email, as before.
const CONSENT = '_alva_withdrawal_consent';
const CONSENT_TEXT = '_alva_withdrawal_consent_text';

/** Consent ticked on this page (not inside the cart), as line properties – or null. */
function pageConsent(source) {
  if (source.closest('cart-drawer-component, cart-items-component, .cart-drawer')) return null;
  const box = [...document.querySelectorAll('.alva-withdrawal-consent__checkbox')].find((c) => c.checked);
  if (!box) return null;
  const text = box.closest('.alva-withdrawal-consent, label')?.querySelector('.alva-withdrawal-consent__text');
  return { [CONSENT]: 'true', [CONSENT_TEXT]: (text?.textContent || '').trim().slice(0, 5000) };
}

const getCart = () =>
  fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

/**
 * Adds lines, or (with `remove`) swaps lines for others. Afterwards the cart's own section HTML is fetched fresh
 * and handed to Horizon, so drawer, cart icon and cart page always show the real cart.
 * @param {Element} source - element the event starts from
 * @param {{id: number, quantity: number, properties?: Record<string, string>}[]} items - lines to add
 * @param {Record<string, number>} [remove] - line keys to set to 0 (a swap)
 */
async function changeCart(source, items, remove) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: remove ? 'update' : 'add',
      context: 'cart',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const before = await getCart();
    let consent = pageConsent(source);
    if (!consent && !remove && source.closest('cart-drawer-component, cart-items-component, .cart-drawer')) {
      // "+ Add" inside the cart: no checkbox there – keep the consent the buyer already gave for this cart
      const given = before.items.find((line) => line.properties && line.properties[CONSENT] === 'true');
      if (given) consent = { [CONSENT]: 'true', [CONSENT_TEXT]: given.properties[CONSENT_TEXT] || '' };
    }
    const removing = remove && Object.keys(remove).length ? remove : null;
    if (removing) {
      // keep consent the swapped-out lines already had
      const withConsent = before.items.find(
        (line) => line.key in removing && line.properties && line.properties[CONSENT] === 'true'
      );
      if (withConsent) consent = { [CONSENT]: 'true', [CONSENT_TEXT]: withConsent.properties[CONSENT_TEXT] || '' };
    }
    // Digital packs: one of each is enough. Anything already in the cart (Add to cart pressed first, a double
    // click, Back from checkout with the ticks still on) is not added again – otherwise the buyer pays twice and
    // Shopify's quantity-based discount step no longer matches what the picker showed.
    const have = new Set(before.items.filter((line) => !(removing && line.key in removing)).map((l) => l.variant_id));
    const fresh = items.filter((i, n) => !have.has(i.id) && items.findIndex((j) => j.id === i.id) === n);
    if (fresh.length) {
      // add before removing, so a failed add never leaves the buyer with fewer packs than before
      const lines = fresh.map((i) => (consent ? { ...i, properties: { ...(i.properties || {}), ...consent } } : i));
      const added = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      }).then((r) => r.json());
      if (added.status) throw new Error(added.description || added.message || 'Could not add');
    }
    if (removing) {
      const res = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: removing }),
      }).then((r) => r.json());
      if (res.status) throw new Error(res.description || res.message || 'Could not update');
    }
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    return false;
  }
  // The lines ARE in the cart now. If only the drawer refresh fails (e.g. Shopify's 429 rate limit) the change
  // still counts – "Checkout now" must go on to checkout instead of standing still.
  try {
    const ids = cartSectionIds();
    const [cart, sections] = await Promise.all([
      getCart(),
      ids.length
        ? fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json())
        : Promise.resolve(undefined),
    ]);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections, source: 'ap-bundle', didError: false },
    });
  } catch (error) {
    console.warn('ap-bundle: cart changed, drawer refresh failed', error);
    deferred.reject(error);
  }
  return true;
}

// "Listen" on a picker row opens that pack's page with the packs the customer ticked THEMSELF ticked there too, so
// they can hear each pack without losing the bundle they're building (owner's idea). Only their own ticks travel –
// never a guess such as the page they came from – so browsing never fills the picker by itself (owner, 2026-10-23).
// A pack ticked here and then opened via Listen stays one of their picks for the next page.
const PICKS = 'ap-picks';
const savePicks = (mine) => {
  const v = [...mine];
  try {
    sessionStorage.setItem(PICKS, JSON.stringify({ v, t: Date.now() }));
  } catch (e) {} // private mode etc.: the link still works, just without the ticks
};
const takePicks = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(PICKS) || 'null');
    sessionStorage.removeItem(PICKS);
    return saved && Date.now() - saved.t < 30 * 60 * 1000 ? saved.v : [];
  } catch (e) {
    return [];
  }
};

/** @param {HTMLElement} picker */
function initPicker(picker) {
  if (picker.dataset.apReady) return;
  picker.dataset.apReady = '1';
  /** the customer's own ticks (variant ids), including ones carried from the page before */
  const mine = new Set(takePicks());
  picker.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach((b) => {
    if (mine.has(b.dataset.variant)) b.checked = true;
  });
  picker.addEventListener('change', (e) => {
    const box = e.target;
    if (!(box instanceof HTMLInputElement) || box.disabled || !box.dataset.variant) return;
    if (box.checked) mine.add(box.dataset.variant);
    else mine.delete(box.dataset.variant);
  });
  picker.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-ap-listen]')) savePicks(mine);
  });

  /** @type {[number, number][]} */
  const tiers = JSON.parse(picker.dataset.tiers || '[]').sort((a, b) => a[0] - b[0]);
  // Same look as Liquid's | money (e.g. "347,00 kr").
  const fmt = (cents) => formatMoney(cents, picker.dataset.moneyFormat || '{{amount}}', picker.dataset.currency || 'USD');
  const boxes = () => [...picker.querySelectorAll('input[type="checkbox"]')];
  const sum = picker.querySelector('[data-ap-pick-sum]');
  const hint = picker.querySelector('[data-ap-pick-hint]');
  const full = picker.querySelector('[data-ap-pick-full]');
  const fullDiff = picker.querySelector('[data-ap-pick-full-diff]');
  const button = picker.querySelector('[data-ap-pick-add]');
  const checkout = picker.querySelector('[data-ap-pick-checkout]');
  const express = picker.querySelector('[data-ap-pick-express]');
  const productInfo = picker.closest('.product-information');
  // the product page's own pack (checked + disabled) is part of the bundle; on the bundle page nothing is preset
  const preset = boxes().filter((b) => b.disabled && b.checked).length;

  const update = () => {
    const chosen = boxes().filter((b) => b.checked);
    const count = chosen.length;
    const total = chosen.reduce((s, b) => s + Number(b.dataset.price), 0);
    const reached = tiers.filter(([min]) => count >= min).pop();
    const next = tiers.find(([min]) => count < min);
    const pct = reached ? reached[1] : 0;
    // Shopify takes the % off each line and drops the fraction of a cent – sum it the same way so the
    // picker shows exactly what the checkout charges (59,50 kr, not 59,48 kr)
    const price = chosen.reduce((s, b) => {
      const p = Number(b.dataset.price);
      return s + p - Math.floor((p * pct) / 100);
    }, 0);

    const picking = count > preset;
    if (count < 2) {
      sum.textContent = '';
      button.disabled = !picking;
      button.textContent = picking ? 'Add to cart' : 'Tick to build a bundle';
    } else {
      sum.innerHTML = `${count} picked <s>${fmt(total)}</s> ${fmt(price)}${pct ? ` · <b>save ${pct}%</b>` : ''}`;
      button.disabled = false;
      button.textContent = `Add all ${count} to cart`;
    }
    if (checkout) {
      checkout.hidden = !picking;
      checkout.textContent = count > 1 ? `Checkout now – all ${count}` : 'Checkout now';
    }
    if (express) express.hidden = !picking;
    // the product's own Add to cart / Apple Pay would buy only this one pack – hide them while picking.
    // They sit above the picker, so hiding/showing them would make the picker jump under the finger (owner, phone):
    // move the page by the same amount so the box just tapped stays where it was.
    if (productInfo && productInfo.classList.contains('ap-picking') !== picking) {
      const before = picker.getBoundingClientRect().top;
      productInfo.classList.toggle('ap-picking', picking);
      const moved = picker.getBoundingClientRect().top - before;
      if (moved) {
        // Horizon scrolls .page-wrapper instead of the window on wide screens (≥ 990 px)
        const wrap = document.querySelector('.page-wrapper');
        const scroller =
          wrap && wrap.scrollHeight > wrap.clientHeight && getComputedStyle(wrap).overflowY !== 'visible' ? wrap : window;
        scroller.scrollBy({ top: moved, behavior: 'instant' });
        // if the product column is pinned (sticky), moving the page didn't move the picker – undo it
        if (Math.abs(picker.getBoundingClientRect().top - before) > Math.abs(moved) / 2) {
          scroller.scrollBy({ top: -moved, behavior: 'instant' });
        }
      }
    }
    hint.textContent = !next
      ? ''
      : count === 0
        ? `Pick any ${next[0]} to save ${next[1]}%`
        : `Tick ${next[0] - count} more for ${next[1]}% off${count >= 2 ? ' all of them' : ''}`;

    if (full) {
      const fullPrice = Number(picker.dataset.fullPrice);
      // offer the buy-it-all bundle at the top step, or as soon as the picked ones cost half of it or more
      const show = count >= 2 && ((reached && reached === tiers[tiers.length - 1]) || price >= fullPrice / 2);
      full.hidden = !show;
      if (show) {
        const diff = fullPrice - price;
        fullDiff.textContent = diff > 0 ? `– only ${fmt(diff)} more` : '– cheaper than these';
      }
    }
  };

  picker.addEventListener('change', update);
  const chosenItems = () =>
    boxes()
      .filter((b) => b.checked)
      .map((b) => ({ id: Number(b.dataset.variant), quantity: 1 }));
  if (checkout) {
    checkout.addEventListener('click', async () => {
      checkout.disabled = true;
      checkout.setAttribute('aria-busy', 'true');
      // everything into the cart (discount + Alva consent come along), then straight to checkout where
      // Apple Pay / Shop Pay / PayPal pay for the whole cart
      const ok = await changeCart(checkout, chosenItems());
      if (ok) window.location.href = '/checkout';
      else {
        checkout.disabled = false;
        checkout.removeAttribute('aria-busy');
        hint.textContent = 'Something went wrong – please try again.';
      }
    });
  }
  button.addEventListener('click', async () => {
    const items = chosenItems();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const ok = await changeCart(button, items);
    button.removeAttribute('aria-busy');
    if (ok) {
      boxes().forEach((b) => {
        if (!b.disabled) b.checked = false;
      });
      mine.clear(); // they're in the cart now
    }
    update();
    if (!ok) hint.textContent = 'Something went wrong – please try again.';
  });
  // Back from checkout: the browser may show this page from its cache with "Checkout now" still switched off
  addEventListener('pageshow', (e) => {
    if (!e.persisted || !checkout) return;
    checkout.disabled = false;
    checkout.removeAttribute('aria-busy');
    update();
  });
  // Build-your-bundle page: the bar is fixed to the bottom of the screen – reserve its height at the end of the
  // page so it never covers the footer
  const bar = picker.querySelector('.ap-bpage__bar');
  if (bar && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      // the space goes at the end of whatever scrolls: .page-wrapper on wide screens (Horizon), else the page
      const wrap = document.querySelector('.page-wrapper');
      const inWrap = wrap && wrap.scrollHeight > wrap.clientHeight && getComputedStyle(wrap).overflowY !== 'visible';
      document.body.style.paddingBottom = inWrap ? '' : `${bar.offsetHeight}px`;
      if (wrap) wrap.style.paddingBottom = inWrap ? `${bar.offsetHeight}px` : '';
    }).observe(bar);
  }
  update();
}

if (!window.apBundleReady) {
  window.apBundleReady = true;

  const boot = () => document.querySelectorAll('[data-ap-picker]').forEach((el) => initPicker(/** @type {HTMLElement} */ (el)));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Ad links like /products/speed#bundle land on the picker, and the picker's "Listen" links (…#listen) on the
  // walkthrough that is shown (phones: under the buy buttons; computers: under the product). Jump as soon as the
  // page is parsed, and once more when images have loaded (they shift it) unless the visitor has scrolled or tapped.
  if (location.hash === '#bundle' || location.hash === '#listen') {
    const target = () =>
      location.hash === '#bundle'
        ? document.getElementById('bundle')
        : [...document.querySelectorAll('[data-ap-walk]')].find((w) => w.offsetParent !== null);
    const jump = () => target()?.scrollIntoView({ block: 'start' });
    let touched = false;
    for (const type of ['wheel', 'touchstart', 'keydown']) addEventListener(type, () => (touched = true), { once: true, passive: true });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', jump, { once: true });
    else jump();
    addEventListener('load', () => touched || jump(), { once: true });
  }
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);

  // Alva's withdrawal box gets a small "?" that explains it. Opened by a tap (phones have no hover), shown as a
  // short line under the box – nothing pops up over the page. If Alva changes its box, the "?" just isn't added.
  const addConsentHelp = () =>
    document.querySelectorAll('.alva-withdrawal-consent').forEach((box, n) => {
      const text = box.querySelector('.alva-withdrawal-consent__text');
      const label = box.querySelector('.alva-withdrawal-consent__label');
      if (!text || !label || box.querySelector('.ap-consent-help')) return;
      const note = document.createElement('p');
      note.className = 'ap-consent-note';
      note.id = `ap-consent-note-${n}`;
      note.hidden = true;
      note.innerHTML =
        'EU law gives you 14 days to cancel online purchases, but that right ends once a digital download starts. ' +
        "You need to agree to this to get your files: tick the box now, or confirm it on the download page after " +
        'checkout.';
      const help = document.createElement('button');
      help.type = 'button';
      help.className = 'ap-consent-help';
      help.textContent = '?';
      help.setAttribute('aria-label', 'What is this?');
      help.setAttribute('aria-expanded', 'false');
      help.setAttribute('aria-controls', note.id);
      help.addEventListener('click', (event) => {
        // inside the label: don't tick the box
        event.preventDefault();
        event.stopPropagation();
        note.hidden = !note.hidden;
        help.setAttribute('aria-expanded', String(!note.hidden));
      });
      text.append(' ', help);
      label.after(note);
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', addConsentHelp);
  else addConsentHelp();
  document.addEventListener('shopify:section:load', addConsentHelp);

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const add = target?.closest('[data-ap-bundle-add]');
    const swap = target?.closest('[data-ap-bundle-swap]');
    const many = target?.closest('[data-ap-bundle-add-many]');
    const button = add || swap || many;
    if (!button) return;
    event.preventDefault();
    // a double click or Enter pressed twice must not send the same packs twice
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    let ok;
    if (add) {
      ok = await changeCart(button, [{ id: Number(add.getAttribute('data-ap-bundle-add')), quantity: 1 }]);
    } else if (many) {
      // "Complete your collection": the packs the customer doesn't own yet.
      const ids = (many.getAttribute('data-ap-bundle-add-many') || '').split(',').filter(Boolean);
      ok = await changeCart(button, ids.map((id) => ({ id: Number(id), quantity: 1 })));
    } else {
      // Replace the separate packs with the full bundle product.
      const remove = Object.fromEntries(
        (swap.getAttribute('data-remove') || '').split(',').filter(Boolean).map((key) => [key, 0])
      );
      ok = await changeCart(button, [{ id: Number(swap.getAttribute('data-ap-bundle-swap')), quantity: 1 }], remove);
    }
    button.removeAttribute('aria-busy');
  });

  // Shopify Forms' "10% off" teaser (bottom left) in the shop's own look and half the size, so it covers less of the
  // page on phones (owner). The app draws it in an open shadow root whose padding can't be set from outside, so a
  // small style is added inside it. Class names are matched loosely; if the app changes them, nothing happens.
  const TEASER_CSS = `
    [class*="appEmbedRootTeaser"] { --spacing-size: 12px; }
    button[class*="teaserContainer"] {
      padding: 8px 14px 8px 10px !important; gap: 14px !important; margin: 0 12px !important;
      border-radius: 0 !important; border: 1px solid #121212 !important; box-shadow: none !important;
    }
    /* the close cross first, with room to "10% off", so tapping the offer doesn't close it (owner) */
    button[class*="teaserContainer"] [class*="formCloseButton"] { order: -1; }
    [class*="teaserHeading"] { font: 500 13px/1.2 Jost, sans-serif !important; letter-spacing: 0.04em !important; }
    button[class*="teaserContainer"] [class*="formCloseButton"] svg { width: 9px !important; height: 9px !important; }`;
  const styleTeaser = () => {
    const root = document.querySelector('shopify-forms-embed')?.shadowRoot;
    if (!root) return false;
    if (!root.getElementById('ap-teaser')) {
      const style = document.createElement('style');
      style.id = 'ap-teaser';
      style.textContent = TEASER_CSS;
      root.appendChild(style);
    }
    return true;
  };
  // the app adds its element some seconds after the page (sometimes 20+): watch for it instead of guessing a time
  if (!styleTeaser()) {
    const watch = new MutationObserver(() => {
      if (styleTeaser()) watch.disconnect();
    });
    watch.observe(document.body, { childList: true, subtree: true });
    customElements.whenDefined('shopify-forms-embed').then(() => setTimeout(() => styleTeaser() && watch.disconnect(), 0));
  }

  // Product cards: play the first demo right in the grid (snippets/ap-card-play.liquid). One sound at a time,
  // shared with the other players through the 'ap:play' event.
  const cardAudio = new Audio();
  cardAudio.preload = 'none';
  let cardButton = null;
  const setCard = (on) => cardButton && cardButton.setAttribute('aria-pressed', String(on));
  cardAudio.addEventListener('play', () => setCard(true));
  cardAudio.addEventListener('pause', () => setCard(false));
  cardAudio.addEventListener('ended', () => setCard(false));
  document.addEventListener('ap:play', (e) => {
    if (e.detail !== cardAudio && !cardAudio.paused) cardAudio.pause();
  });
  document.addEventListener(
    'click',
    (event) => {
      const play = event.target instanceof Element ? event.target.closest('[data-ap-card-play]') : null;
      if (!play) return;
      // the card itself is a link – don't open the product
      event.preventDefault();
      event.stopPropagation();
      if (play === cardButton && !cardAudio.paused) {
        cardAudio.pause();
        return;
      }
      document.dispatchEvent(new CustomEvent('ap:play', { detail: cardAudio }));
      setCard(false);
      cardButton = play;
      cardAudio.src = play.getAttribute('data-ap-card-play');
      cardAudio.play();
    },
    true
  );
}

// One of each: every product here is a download, so a second copy of the same pack gives the buyer nothing (order
// #7465 paid for Speed and Gossamer twice, and Shopify's "any 5" discount counts copies, not packs). The quantity
// selector is gone for downloads, gift cards and shipped products keep it (snippets/quantity-selector.liquid); here "Add to cart" on a pack
// that's already in the cart just opens the cart, and if a second copy still gets in (double click, an old cart,
// a /cart/add link) the line is set back to 1.
if (!window.apOneEachReady) {
  window.apOneEachReady = true;

  /** variant ids of downloads in the cart (gift cards and shipped products left out) – from the page, then from /cart.js after every change */
  const inCart = new Set(window.apCartVariants || []);
  let fixing = false;

  const sync = async () => {
    const cart = await getCart();
    inCart.clear();
    for (const line of cart.items) if (!line.gift_card && !line.requires_shipping) inCart.add(line.variant_id);
    const extra = cart.items.filter((line) => !line.gift_card && !line.requires_shipping && line.quantity > 1);
    if (!extra.length || fixing) return;
    fixing = true;
    const ones = Object.fromEntries(extra.map((line) => [line.key, 1]));
    await changeCart(document.querySelector('cart-items-component') || document.body, [], ones);
    fixing = false;
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const after = () => sync().catch((error) => console.warn('ap-bundle: one-each check failed', error));
    event.promise ? event.promise.then(after, after) : after();
  });
  if (window.apCartHasExtra) sync().catch(() => {});

  const openCart = () => {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else window.location.href = Theme.routes.cart_url;
  };

  /** The product form's variant if it is already in the cart, else null. */
  const alreadyIn = (el) => {
    const form = el.closest('product-form-component')?.querySelector('form');
    const id = Number(form?.querySelector('input[name="id"]')?.value);
    return id && inCart.has(id) ? id : null;
  };

  // capture phase: runs before Horizon's own click/submit handlers, so no fly-to-cart animation and no request
  const guard = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const from = event.type === 'click' ? target?.closest('add-to-cart-component button') : target;
    if (!from || !from.closest('product-form-component')) return;
    if (alreadyIn(from)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openCart();
    } else if (event.type === 'submit') {
      // counted at once, so a second click before Shopify answers doesn't add it again; sync() corrects it after
      const form = from.closest('product-form-component')?.querySelector('form');
      const id = Number(form?.querySelector('input[name="id"]')?.value);
      // (gift cards keep their quantity selector and may be bought more than once)
      if (id && !from.closest('product-form-component')?.querySelector('quantity-selector-component')) inCart.add(id);
    }
  };
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);
}
